import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import TwoFactorVerificationModal from './TwoFactorVerificationModal';
import { useAuthBranding } from '../hooks/useAuthBranding';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from './common/PasswordRequirements';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFAUserId, setTwoFAUserId] = useState<number | null>(null);
  const [twoFAMethod, setTwoFAMethod] = useState<'app' | 'sms' | 'both'>('app');
  const [twoFAPhone, setTwoFAPhone] = useState<string | undefined>();

  // Focus states
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);
const [linkHovered, setLinkHovered] = useState(false);

  // Button hover
  const [submitHovered, setSubmitHovered] = useState(false);
  const { branding, brandingLoaded } = useAuthBranding('Reset Password');

  const token = searchParams.get('token');

// Password validation — driven by the server policy. Breach and name/email
  // screening are server-side only, so allRequirementsMet does not guarantee
  // the server will accept the password.
  const { policy: passwordPolicy } = usePasswordPolicy();
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const { meetsClientChecks: allRequirementsMet } = evaluatePassword(
    password,
    passwordPolicy
  );


  useEffect(() => {
    if (!token) {
      setError('Invalid reset link - no token found');
      setVerifying(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await apiService.get(`/auth/password/verify-token/${token}`);
      if (response.data.valid) {
        setUserEmail(response.data.email);
      } else {
        setError('Invalid or expired reset link');
      }
    } catch (err: any) {
      setError('Invalid or expired reset link');
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent | null, twoFactorCode?: string, twoFactorMethod?: string) => {
    if (e) e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = { token, password };
      
      if (twoFactorCode && twoFactorMethod) {
        payload.twoFactorCode = twoFactorCode;
        payload.twoFactorMethod = twoFactorMethod;
      }

      const response = await apiService.post('/auth/password/reset', payload);

      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        setRequires2FA(true);
        setTwoFAUserId(response.data.userId);
        setTwoFAMethod(response.data.method);
        setTwoFAPhone(response.data.phoneNumber);
        setLoading(false);
        return;
      }

      // Success
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
      setLoading(false);
    }
  };

  const handle2FASuccess = async (code: string, method: string) => {
    await handleSubmit(null, code, method);
  };

  // Styles
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 24px 0',
    textAlign: 'center',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 20px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const getSecondaryButtonStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const alertStyle = (type: 'error' | 'success'): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: `4px solid ${type === 'error' ? 'var(--v2-error)' : 'var(--v2-success)'}`,
    backgroundColor: type === 'error' ? 'var(--v2-error-bg)' : 'var(--v2-success-bg)',
    color: type === 'error' ? 'var(--v2-error)' : 'var(--v2-success-dark)',
    fontSize: '0.875rem',
    marginBottom: '20px',
  });


  // Verifying state
  if (verifying) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={pageStyle}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Loader2 size={48} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
              <p style={{ ...subtitleStyle, marginBottom: 0 }}>Verifying reset link...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Invalid token state
  if (error && !userEmail) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={titleStyle}>{branding?.company_name || 'DashboardFox'}</h1>
            <p style={{ ...subtitleStyle, color: 'var(--v2-error)', fontWeight: 600 }}>Invalid Reset Link</p>
            
            <div style={{ ...alertStyle('error'), width: '100%', boxSizing: 'border-box' }}>
              {error}
            </div>

            <button
              onClick={() => navigate('/auth/forgot-password')}
              onMouseEnter={() => setLinkHovered(true)}
              onMouseLeave={() => setLinkHovered(false)}
              style={getSecondaryButtonStyle(linkHovered)}
            >
              Request New Reset Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={titleStyle}>DashboardFox</h1>
            <p style={{ ...subtitleStyle, color: 'var(--v2-success)', fontWeight: 600 }}>Password Reset Successful</p>
            
            <div style={{ ...alertStyle('success'), width: '100%', boxSizing: 'border-box' }}>
              Your password has been reset successfully. Redirecting to login...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  const isSubmitDisabled = loading || !password || !confirmPassword || !allRequirementsMet || !passwordsMatch;

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Reset Password</h1>
          <p style={subtitleStyle}>
            Enter your new password for <strong style={{ color: 'var(--v2-text)' }}>{userEmail}</strong>
          </p>

          {error && (
            <div style={alertStyle('error')}>
              {error}
            </div>
          )}

          <form onSubmit={(e) => handleSubmit(e)}>
            {/* New Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                disabled={loading}
                autoComplete="new-password"
                autoFocus
                style={getInputStyle(passwordFocused)}
              />
            </div>

{/* Password requirements */}
            <PasswordRequirements
              password={password}
              policy={passwordPolicy}
              columns={1}
              style={{ marginBottom: '20px' }}
            />

            {/* Confirm Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setConfirmFocused(true)}
                onBlur={() => setConfirmFocused(false)}
                disabled={loading}
                autoComplete="new-password"
                style={getInputStyle(confirmFocused)}
              />
              {confirmPassword && !passwordsMatch && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', marginTop: '6px', marginBottom: 0 }}>
                  Passwords do not match
                </p>
              )}
              {passwordsMatch && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-success)', marginTop: '6px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Check size={14} strokeWidth={2} /> Passwords match
                </p>
              )}
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '-12px 0 20px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={showPassword} onChange={(e) => setShowPassword(e.target.checked)} />
              Show passwords
            </label>
            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitDisabled}
              onMouseEnter={() => setSubmitHovered(true)}
              onMouseLeave={() => setSubmitHovered(false)}
              style={getButtonStyle(submitHovered, isSubmitDisabled)}
            >
              {loading ? (
                <>
                  <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Resetting...</span>
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* 2FA Verification Modal */}
      {requires2FA && twoFAUserId && (
        <TwoFactorVerificationModal
          open={requires2FA}
          userId={twoFAUserId}
          availableMethods={twoFAMethod}
          phoneNumber={twoFAPhone}
          onSuccess={handle2FASuccess}
          onCancel={() => {
            setRequires2FA(false);
            setError('Password reset cancelled');
            setLoading(false);
          }}
        />
      )}
    </>
  );
};

export default ResetPassword;
